import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import SectorDetailsDialog from './SectorDetails';
import axiosInstance from '../services/axiosInstance';
import { Briefcase, TrendingUp, TrendingDown, Layers, AlertCircle, RefreshCw, Activity, DollarSign, Trash2, Search, X } from 'lucide-react';

// ShadCN UI Components
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';

const SectorCards = () => {
    const [sectorData, setSectorData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedSector, setSelectedSector] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [sortBy, setSortBy] = useState('totalInvestment'); // Default sort
    const [sortOrder, setSortOrder] = useState('desc'); // Default order
    const [filterValue, setFilterValue] = useState('');
    const [animateCards, setAnimateCards] = useState(false);
    
    // Enhanced delete state
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [sectorToDelete, setSectorToDelete] = useState(null);
    const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
    const [userInputText, setUserInputText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    // Enhanced delete function
    const handleDeleteSector = (sector) => {
        setSectorToDelete(sector);
        setDeleteConfirmationText(generateRandomText());
        setUserInputText('');
        setDeleteModalOpen(true);
    };

    const generateRandomText = () => {
        const words = ['DELETE', 'REMOVE', 'PERMANENT', 'IRREVERSIBLE', 'CONFIRM', 'WARNING'];
        const randomWord = words[Math.floor(Math.random() * words.length)];
        return randomWord;
    };

    const handleConfirmDelete = async () => {
        if (userInputText !== deleteConfirmationText) {
            return;
        }

        try {
            setIsDeleting(true);
            const response = await axiosInstance.delete(`/stocks/sector/${sectorToDelete.sector}`);
            
            if (response.data.success) {
                // Remove the sector from the local state
                setSectorData(prevData => 
                    prevData.filter(sector => sector.sector !== sectorToDelete.sector)
                );
                setDeleteModalOpen(false);
                setSectorToDelete(null);
                setDeleteConfirmationText('');
                setUserInputText('');
            } else {
                throw new Error(response.data.message || 'Failed to delete sector');
            }
        } catch (error) {
            console.error('Error deleting sector:', error);
            alert(error.response?.data?.message || error.message || 'Failed to delete sector');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleCancelDelete = () => {
        setDeleteModalOpen(false);
        setSectorToDelete(null);
        setDeleteConfirmationText('');
        setUserInputText('');
    };

    // Delete Confirmation Modal Component
    const DeleteConfirmationModal = () => {
        if (!deleteModalOpen || !sectorToDelete) return null;

        return createPortal(
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[2000] p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden ring-1 ring-slate-200">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-6">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-red-400 rounded-xl flex items-center justify-center">
                                <AlertCircle size={20} className="text-white" />
                            </div>
                            <h3 className="text-xl font-bold">Delete Sector</h3>
                        </div>
                    </div>
                    
                    {/* Body */}
                    <div className="p-6 space-y-4">
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                            <p className="text-red-800 font-semibold mb-2">⚠️ Warning: This action cannot be undone!</p>
                            <p className="text-red-700 mb-3">
                                You are about to delete the <span className="font-bold">{sectorToDelete.sector}</span> sector and all its associated data:
                            </p>
                            <ul className="text-sm text-red-600 space-y-1 list-disc list-inside">
                                <li>All stocks in this sector</li>
                                <li>All buy/sell transactions</li>
                                <li>Watch price settings</li>
                                <li>Investment history</li>
                                <li>Total investment: ₹{sectorToDelete.totalInvestment?.toLocaleString('en-IN') || '0'}</li>
                                <li>Total stocks: {sectorToDelete.totalStocks || 0}</li>
                            </ul>
                        </div>
                        
                        <div className="space-y-3">
                            <p className="text-slate-700">
                                To confirm deletion, please type: <span className="font-bold text-red-600">{deleteConfirmationText}</span>
                            </p>
                            <Input
                                type="text"
                                value={userInputText}
                                onChange={(e) => setUserInputText(e.target.value)}
                                placeholder={`Type "${deleteConfirmationText}" to confirm`}
                                className="border-red-300 focus:border-red-500 focus:ring-red-500"
                                autoFocus
                            />
                        </div>
                    </div>
                    
                    {/* Footer */}
                    <div className="bg-slate-50 px-6 py-4 flex gap-3 justify-end">
                        <Button
                            variant="outline"
                            onClick={handleCancelDelete}
                            disabled={isDeleting}
                            className="border-slate-300 hover:bg-slate-100"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmDelete}
                            disabled={userInputText !== deleteConfirmationText || isDeleting}
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            {isDeleting ? (
                                <>
                                    <RefreshCw size={16} className="mr-2 animate-spin" />
                                    Deleting...
                                </>
                            ) : (
                                <>
                                    <Trash2 size={16} className="mr-2" />
                                    Delete Sector
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </div>,
            document.body
        );
    };

    // Get gradient colors for stock pills
    const getStockColor = (index) => {
        const colors = [
            'bg-gradient-to-r from-pink-100 to-rose-100 text-pink-700 border border-pink-200',
            'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 border border-blue-200',
            'bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 border border-green-200',
            'bg-gradient-to-r from-purple-100 to-violet-100 text-purple-700 border border-purple-200',
            'bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-700 border border-yellow-200',
            'bg-gradient-to-r from-indigo-100 to-blue-100 text-indigo-700 border border-indigo-200',
            'bg-gradient-to-r from-teal-100 to-cyan-100 text-teal-700 border border-teal-200',
            'bg-gradient-to-r from-orange-100 to-red-100 text-orange-700 border border-orange-200'
        ];
        return colors[index % colors.length];
    };

    useEffect(() => {
        fetchSectorAnalytics();
    }, []);

    // Animation effect when data loads
    useEffect(() => {
        if (!loading && sectorData.length > 0) {
            setAnimateCards(true);
        }
    }, [loading, sectorData]);

    const fetchSectorAnalytics = async () => {
        try {
            setLoading(true);
            setAnimateCards(false);

            // Using axiosInstance instead of fetch
            const response = await axiosInstance.get('/analytics/sector-analytics');
            const data = response.data;

            if (data.success && Array.isArray(data.sectorAnalytics)) {
                setSectorData(data.sectorAnalytics);
            } else {
                throw new Error('Invalid data format received from server');
            }
        } catch (error) {
            console.error('Error fetching sector data:', error);
            setError(error.response?.data?.message || error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenSectorDetails = (sectorName) => {
        if (!sectorName) return;
        setSelectedSector(sectorName);
        setIsDialogOpen(true);
    };

    // Sort function for sectors
    const sortSectors = (a, b) => {
        if (sortBy === 'sector') {
            return sortOrder === 'asc'
                ? a.sector.localeCompare(b.sector)
                : b.sector.localeCompare(a.sector);
        }

        return sortOrder === 'asc'
            ? a[sortBy] - b[sortBy]
            : b[sortBy] - a[sortBy];
    };

    // Filter function
    const filterSectors = (sector) => {
        if (!filterValue) return true;

        const searchTerm = filterValue.toLowerCase();
        const sectorName = sector.sector.toLowerCase();
        const stockNames = sector.stocks.map(s => s.stockName.toLowerCase());

        return sectorName.includes(searchTerm) ||
            stockNames.some(name => name.includes(searchTerm));
    };

    // Handle sort change
    const handleSortChange = (criteria) => {
        if (sortBy === criteria) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(criteria);
            setSortOrder('desc');
        }
    };

    // Format currency with appropriate symbol and number formatting
    const formatCurrency = (value) => {
        return `₹${Math.abs(value).toLocaleString('en-IN')}`;
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-16">
                <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
                <p className="text-lg font-medium text-slate-600">Loading investment data...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center mb-4">
                    <AlertCircle size={32} className="text-white" />
                </div>
                <p className="text-lg font-semibold text-slate-900 mb-2">Oops! Something went wrong</p>
                <p className="text-slate-600 mb-4">Error: {error}</p>
                <Button 
                    onClick={fetchSectorAnalytics} 
                    className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                >
                    <RefreshCw size={16} className="mr-2" /> 
                    Try Again
                </Button>
            </div>
        );
    }

    if (!sectorData.length) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mb-4">
                    <Briefcase size={32} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">No Sector Data Available</h3>
                <p className="text-slate-600 mb-4">Start by adding some stocks to your portfolio!</p>
                <Button 
                    onClick={fetchSectorAnalytics} 
                    className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                >
                    <RefreshCw size={16} className="mr-2" /> 
                    Refresh Data
                </Button>
            </div>
        );
    }

    // Filter and sort the data
    const processedData = [...sectorData]
        .filter(filterSectors)
        .sort(sortSectors);

    return (
        <div style={{ 
            all: 'unset', 
            fontFamily: 'system-ui, -apple-system, sans-serif',
            color: '#111827',
            backgroundColor: 'transparent'
        }}>
            <div className="space-y-6">
                {/* Controls - Sort and Filter */}
                <div className="space-y-4">
                    {/* Search Bar */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                        <Input
                            type="text"
                            placeholder="Search sectors or stocks..."
                            value={filterValue}
                            onChange={(e) => setFilterValue(e.target.value)}
                            className="pl-10 pr-10 h-12 border-indigo-200 focus:border-indigo-500 focus:ring-indigo-500 bg-white/80 backdrop-blur-sm"
                        />
                        {filterValue && (
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setFilterValue('')}
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 w-6 h-6"
                            >
                                <X size={16} />
                            </Button>
                        )}
                    </div>

                    {/* Sort Buttons */}
                    <div className="flex flex-wrap gap-2">
                        <SortButton
                            label="Investment"
                            value="totalInvestment"
                            currentSort={sortBy}
                            order={sortOrder}
                            onClick={handleSortChange}
                            icon={<DollarSign size={16} />}
                        />
                        <SortButton
                            label="Returns"
                            value="totalReturns"
                            currentSort={sortBy}
                            order={sortOrder}
                            onClick={handleSortChange}
                            icon={<TrendingUp size={16} />}
                        />
                        <SortButton
                            label="P/L"
                            value="profitLoss"
                            currentSort={sortBy}
                            order={sortOrder}
                            onClick={handleSortChange}
                            icon={<Activity size={16} />}
                        />
                        <SortButton
                            label="Name"
                            value="sector"
                            currentSort={sortBy}
                            order={sortOrder}
                            onClick={handleSortChange}
                            icon={<Layers size={16} />}
                        />
                    </div>
                </div>
                {/* Data summary - Overview */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                        {
                            title: "Total Investment",
                            value: formatCurrency(processedData.reduce((sum, s) => sum + s.totalInvestment, 0)),
                            icon: <Briefcase size={20} />,
                            gradient: "from-blue-500 to-blue-600",
                            bg: "from-blue-50 to-blue-100"
                        },
                        {
                            title: "Total Returns",
                            value: formatCurrency(processedData.reduce((sum, s) => sum + s.totalReturns, 0)),
                            icon: <TrendingUp size={20} />,
                            gradient: "from-emerald-500 to-emerald-600",
                            bg: "from-emerald-50 to-emerald-100"
                        },
                        {
                            title: "Net Profit/Loss",
                            value: formatCurrency(processedData.reduce((sum, s) => sum + s.profitLoss, 0)),
                            icon: processedData.reduce((sum, s) => sum + s.profitLoss, 0) >= 0 ? <TrendingUp size={20} /> : <TrendingDown size={20} />,
                            gradient: processedData.reduce((sum, s) => sum + s.profitLoss, 0) >= 0 ? "from-emerald-500 to-emerald-600" : "from-red-500 to-red-600",
                            bg: processedData.reduce((sum, s) => sum + s.profitLoss, 0) >= 0 ? "from-emerald-50 to-emerald-100" : "from-red-50 to-red-100"
                        },
                        {
                            title: "Sectors Tracked",
                            value: processedData.length,
                            icon: <Layers size={20} />,
                            gradient: "from-indigo-500 to-purple-600",
                            bg: "from-indigo-50 to-purple-100"
                        }
                                        ].map((stat, idx) => (
                        <Card key={idx} className={`bg-gradient-to-br ${stat.bg} border-0 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105`}>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${stat.gradient} flex items-center justify-center shadow-lg`}>
                                        <div className="text-white">{stat.icon}</div>
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-xs font-medium text-slate-600">{stat.title}</p>
                                        <p className="text-lg font-bold text-slate-900">{stat.value}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {/* Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {processedData.map((sector, index) => (
                        <Card
                            key={index}
                            className="bg-gradient-to-br from-white via-indigo-50/20 to-purple-50/20 border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 cursor-pointer backdrop-blur-sm ring-1 ring-indigo-200/30"
                            style={{
                                animationDelay: `${index * 100}ms`,
                                opacity: animateCards ? 1 : 0,
                                transform: `translateY(${animateCards ? '0' : '20px'})`,
                                transition: 'opacity 0.5s ease, transform 0.5s ease, box-shadow 0.3s ease, scale 0.3s ease'
                            }}
                            onClick={() => handleOpenSectorDetails(sector.sector)}
                        >
                            <CardHeader className="pb-2 px-4 pt-4">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                                            <Layers className="w-3 h-3 text-white" />
                                        </div>
                                        {sector.sector}
                                    </CardTitle>
                                    <div className="flex items-center gap-1">
                                        <span className="bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-700 px-2 py-1 rounded-full text-xs font-medium border border-indigo-200">
                                            {sector.totalStocks}
                                        </span>
                                        <Button 
                                            variant="ghost"
                                            size="icon"
                                        onClick={(e) => {
                                                e.stopPropagation();
                                            handleDeleteSector(sector);
                                        }}
                                            className="text-red-500 hover:text-red-700 hover:bg-red-100 w-6 h-6"
                                    >
                                            <Trash2 size={12} />
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>

                            <CardContent className="px-4 pb-4 space-y-3">
                                {/* Financial Information */}
                                <div className="space-y-1.5">
                                    <div className="flex justify-between items-center">
                                        <span className="text-slate-600 text-xs font-medium">Investment</span>
                                        <span className="text-slate-900 text-sm font-semibold">{formatCurrency(sector.totalInvestment)}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-slate-600 text-xs font-medium">Returns</span>
                                        <span className="text-slate-900 text-sm font-semibold">{formatCurrency(sector.totalReturns)}</span>
                                    </div>
                                    <div className="flex justify-between items-center px-2 py-1.5 bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg">
                                        <span className="text-slate-700 text-xs font-semibold">P/L</span>
                                        <span className={`font-bold text-sm ${sector.profitLoss >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                            {sector.profitLoss >= 0 ? '+' : ''}{formatCurrency(sector.profitLoss)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-slate-600 text-xs font-medium">ROI</span>
                                        <span className={`font-semibold text-sm ${sector.profitLoss >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                            {((sector.profitLoss / sector.totalInvestment) * 100).toFixed(2)}%
                                        </span>
                                    </div>
                                    
                                    {/* Quantity Information */}
                                    <div className="grid grid-cols-2 gap-2 pt-1">
                                        <div className="text-center py-1.5 px-2 bg-blue-50 rounded-lg">
                                            <div className="text-xs text-slate-600">Bought</div>
                                            <div className="text-sm font-bold text-blue-700">
                                            {sector.stocks.reduce((acc, stock) => acc + stock.totalBoughtQty, 0)}
                                            </div>
                                    </div>
                                        <div className="text-center py-1.5 px-2 bg-green-50 rounded-lg">
                                            <div className="text-xs text-slate-600">Sold</div>
                                            <div className="text-sm font-bold text-green-700">
                                            {sector.stocks.reduce((acc, stock) => acc + stock.totalSoldQty, 0)}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                                                {/* Top Stocks */}
                                <div>
                                    <h4 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-1.5">
                                        <TrendingUp className="w-4 h-4 text-indigo-600" />
                                        Top Stocks
                                    </h4>
                                    <div className="grid grid-cols-2 gap-1.5">
                                        {Array.isArray(sector.stocks) && sector.stocks.length > 0 ? (
                                            sector.stocks.slice(0, 6).map((stock, idx) => (
                                                <div key={idx} className={`p-2 rounded-lg ${getStockColor(idx)} transition-all duration-200 hover:scale-105`}>
                                                    <div className="text-center">
                                                        <div className="font-medium text-xs truncate" title={stock.stockName}>
                                                            {stock.stockName.length > 8 ? stock.stockName.substring(0, 8) + '...' : stock.stockName}
                                                        </div>
                                                        <div className="flex justify-center gap-1 mt-1">
                                                            {stock.qtyLeftToSell > 0 && (
                                                                <span className="bg-white/60 px-1.5 py-0.5 rounded text-xs">
                                                                    S:{stock.qtyLeftToSell}
                                                                </span>
                                                            )}
                                                            {stock.qtyLeftToBuy > 0 && (
                                                                <span className="bg-white/60 px-1.5 py-0.5 rounded text-xs">
                                                                    B:{stock.qtyLeftToBuy}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="col-span-2 text-slate-500 italic text-center py-3 text-sm">
                                                No stocks available
                                            </div>
                                        )}
                                        {sector.stocks.length > 6 && (
                                            <div className="col-span-2 p-2 bg-gradient-to-r from-slate-100 to-slate-200 rounded-lg text-center">
                                                <span className="text-slate-700 font-medium text-xs">
                                                    +{sector.stocks.length - 6} more
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </div>

            {/* Sector Details Dialog */}
            <SectorDetailsDialog
                isOpen={isDialogOpen && !!selectedSector}
                onClose={() => {
                    setIsDialogOpen(false);
                    setSelectedSector(null);
                }}
                sector={selectedSector}
            />

            {/* Delete Sector Dialog */}
            <DeleteConfirmationModal />
        </div>
    );
};

// Sort Button Component
const SortButton = ({ label, value, currentSort, order, onClick, icon }) => {
    const isActive = currentSort === value;

    return (
        <Button
            variant={isActive ? "default" : "outline"}
            onClick={() => onClick(value)}
            className={`${
                isActive 
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-lg' 
                    : 'border-indigo-200 text-slate-700 hover:bg-indigo-50 hover:border-indigo-300'
            } transition-all duration-200 rounded-xl`}
        >
            <span className="flex items-center gap-2">
                {icon}
                {label}
                {isActive && (
                    <span className="text-white">
                        {order === 'asc' ? '↑' : '↓'}
                    </span>
                )}
            </span>
        </Button>
    );
};

export default SectorCards;
